import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import translations from '../translations/utils.translations.json';
import { useSystemClassifiers } from '../../configuration';

const { addTranslations, t } = i18nNsFactory('validation-utils');
addTranslations(translations);

export interface AlternativeFilters {
  alternativeAlgorithms: string | null;
  code: string | null;
  exceptionsCount: string | null;
  name: string | null;
}

export const defaultAlternativeFilters: AlternativeFilters = {
  alternativeAlgorithms: null,
  code: null,
  exceptionsCount: null,
  name: null,
};

export interface RequiredKeyFilters {
  code: string | null;
  key: string | null;
  name: string | null;
  statusId: number[] | null;
  recipientIds: number[] | null;
  ruleTypeId: number[] | null;
  validPeriod: [string | null, string | null] | null;
  voluntary: boolean | null;
  description: string | null;
}

export const defaultRequiredKeyFilters: RequiredKeyFilters = {
  code: null,
  key: null,
  name: null,
  statusId: null,
  recipientIds: null,
  ruleTypeId: null,
  voluntary: null,
  validPeriod: null,
  description: null,
};

export interface ValidationFilters {
  code: string | null;
  condition: string | null;
  description: string | null;
  errorDescription: string | null;
  errorLevelId: number[] | null;
  expressionLeft: string | null;
  expressionOperatorId: number[] | null;
  expressionRight: string | null;
  groupCode: string | null;
  ignoreError: boolean[] | null;
  ignoreReason: string | null;
  statusId: number[] | null;
  validPeriod: string | null;
}

export const defaultValidationFilters: ValidationFilters = {
  code: null,
  condition: null,
  description: null,
  errorDescription: null,
  errorLevelId: null,
  expressionLeft: null,
  expressionOperatorId: null,
  expressionRight: null,
  groupCode: null,
  ignoreError: null,
  ignoreReason: null,
  statusId: null,
  validPeriod: null,
};

export function useValidationRuleOptions() {
  const {
    VALIDATION_ERROR_LEVEL: { systemClassifierItems: errorItems },
    VALIDATION_RULE_OPERATOR: { systemClassifierItems: operatorItems },
    VALIDATION_RULE_STATUS: { systemClassifierItems: statusItems },
  } = useSystemClassifiers(
    'VALIDATION_ERROR_LEVEL',
    'VALIDATION_RULE_OPERATOR',
    'VALIDATION_RULE_STATUS'
  );

  return useMemo(
    () => ({
      errorLevelId: errorItems,
      expressionOperatorId: operatorItems,
      ignoreError: [
        { id: true, name: t('label.yes') },
        { id: false, name: t('label.no') },
      ],
      statusId: statusItems,
    }),
    [errorItems, operatorItems, statusItems]
  );
}

export function useAlternativeFilter() {
  const [alternativeFilters, setAlternativeFilters] = useState(
    defaultAlternativeFilters
  );

  return { alternativeFilters, setAlternativeFilters };
}

export function useKeysFilter() {
  const [requiredKeyFilters, setRequiredKeyFilters] = useState(
    defaultRequiredKeyFilters
  );

  return { requiredKeyFilters, setRequiredKeyFilters };
}

export function useValidationFilter() {
  const [filters, setFilters] = useState(defaultValidationFilters);

  const filterCount = useMemo(
    () => Object.values(filters).filter((value) => value !== null).length,
    [filters]
  );

  return { filterCount, filters, setFilters };
}

export const ValidationTableWrapper = styled.div`
  margin: auto;
  align-items: end;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
